export class CustomerApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body: unknown,
  ) {
    super(message);
    this.name = 'CustomerApiError';
  }
}

export async function customerApi<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // ignore
    }
    throw new CustomerApiError(`API ${res.status}`, res.status, body);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Packs + memberships
// ---------------------------------------------------------------------------

export interface PackOffering {
  id: string;
  accountId: string;
  name: string;
  description: string | null;
  packSize: number;
  priceMinor: number;
  currency: string;
  expiryMonths: number;
  active: number;
  position: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreditPack {
  id: string;
  accountId: string;
  customerId: string;
  offeringId: string;
  packSize: number;
  visitsRemaining: number;
  priceMinor: number;
  currency: string;
  purchasedPaymentId: string;
  expiresAt: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface MembershipTier {
  id: string;
  accountId: string;
  name: string;
  description: string | null;
  visitsPerCycle: number;
  cycleLength: string;
  priceMinor: number;
  currency: string;
  stripeProductId: string;
  stripePriceId: string;
  rolloverUnused: number;
  active: number;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerMembership {
  id: string;
  accountId: string;
  customerId: string;
  tierId: string;
  stripeSubscriptionId: string;
  status: string;
  visitsRemaining: number;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: number;
  createdAt: string;
  updatedAt: string;
}

export interface PacksListResponse {
  offerings: PackOffering[];
  active_packs: CreditPack[];
  memberships: CustomerMembership[];
  tiers: MembershipTier[];
  stripe_account_id: string | null;
}

export interface PurchasePackResponse {
  client_secret: string;
  payment_intent_id: string;
}

export const packsApi = {
  list(): Promise<PacksListResponse> {
    return customerApi<PacksListResponse>('/api/customer/packs');
  },

  purchase(offeringId: string): Promise<PurchasePackResponse> {
    return customerApi<PurchasePackResponse>('/api/customer/packs', {
      method: 'POST',
      body: JSON.stringify({ offering_id: offeringId }),
    });
  },

  subscribeMembership(tierId: string): Promise<{ url: string }> {
    return customerApi<{ url: string }>('/api/customer/memberships', {
      method: 'POST',
      body: JSON.stringify({ tier_id: tierId }),
    });
  },

  membershipPortalLink(): Promise<{ url: string }> {
    return customerApi<{ url: string }>('/api/customer/memberships/portal-link', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
};

// ---------------------------------------------------------------------------
// Refund requests
// ---------------------------------------------------------------------------

export interface CustomerRefundRequest {
  id: string;
  accountId: string;
  customerId: string;
  bookingId: string | null;
  paymentId: string;
  requestedAmountMinor: number;
  approvedAmountMinor: number | null;
  reason: string;
  staffNote: string | null;
  status: string;
  decidedByMembershipId: string | null;
  decidedAt: string | null;
  refundPaymentId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RefundSuggestion {
  suggested_minor: number;
  refund_pct_applied: number;
  paid_so_far_minor: number;
}

export interface RequestRefundBody {
  reason: string;
  requested_amount_minor?: number;
}

export const customerRefundsApi = {
  getRefundSuggestion(bookingId: string): Promise<RefundSuggestion> {
    return customerApi<RefundSuggestion>(`/api/customer/bookings/${bookingId}/refund-suggestion`);
  },

  requestRefund(
    bookingId: string,
    body: RequestRefundBody,
  ): Promise<{ refund_request: CustomerRefundRequest }> {
    return customerApi<{ refund_request: CustomerRefundRequest }>(
      `/api/customer/bookings/${bookingId}/refund-request`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
  },
};

// ---------------------------------------------------------------------------
// Daycare cancel (sub-spec 04 Plan 5 M4)
// ---------------------------------------------------------------------------

export interface DaycareCancelPreview {
  bookingId: string;
  refundMinor: number;
  refundPct: number;
  paidSoFarMinor: number;
  currency: string;
}

export interface DaycareCancelResult {
  ok: boolean;
  bookingId: string;
  refundMinor: number;
  refundSource: 'account_credit' | 'none';
  cancellation: { refundPct: number; paidSoFarMinor: number };
}

export const daycareCancelApi = {
  getPreview(bookingId: string): Promise<DaycareCancelPreview> {
    return customerApi<DaycareCancelPreview>(
      `/api/customer/bookings/${bookingId}/daycare-cancel-preview`,
    );
  },

  cancel(bookingId: string, reason?: string): Promise<DaycareCancelResult> {
    return customerApi<DaycareCancelResult>(`/api/customer/bookings/${bookingId}/daycare-cancel`, {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    });
  },
};

// ---------------------------------------------------------------------------
// Grooming cancel (sub-spec 05 Plan 5 M2)
// ---------------------------------------------------------------------------

export interface GroomingCancelPreview {
  bookingId: string;
  refundMinor: number;
  refundPct: number;
  paidSoFarMinor: number;
  currency: string;
}

export interface GroomingCancelResult {
  ok: boolean;
  bookingId: string;
  refundMinor: number;
  refundSource: 'account_credit' | 'none';
  cancellation: { refundPct: number; paidSoFarMinor: number };
}

export const groomingCancelApi = {
  getPreview(bookingId: string): Promise<GroomingCancelPreview> {
    return customerApi<GroomingCancelPreview>(
      `/api/customer/grooming-appointments/${bookingId}/cancel-preview`,
    );
  },

  cancel(bookingId: string, reason?: string): Promise<GroomingCancelResult> {
    return customerApi<GroomingCancelResult>(
      `/api/customer/grooming-appointments/${bookingId}/cancel`,
      {
        method: 'POST',
        body: JSON.stringify(reason ? { reason } : {}),
      },
    );
  },
};
